import { useCurrentSubscription } from '@/hooks/useSubscription';

export default function UsageStats() {
  const { data: subscription, isLoading } = useCurrentSubscription();

  if (isLoading || !subscription) return null;

  const { limits, usage_today, usage_lifetime, tier } = subscription;

  // Build stats based on tier type
  const stats: Array<{
    label: string;
    current: number;
    max: number | null;
    isLifetime?: boolean;
  }> = [];

  if (tier === 'free') {
    // Free tier shows lifetime usage
    stats.push({
      label: 'Moves generated (lifetime)',
      current: usage_lifetime.tasks_generated,
      max: limits.max_tasks_lifetime,
      isLifetime: true,
    });
    stats.push({
      label: 'Message threads (lifetime)',
      current: usage_lifetime.message_threads_initiated,
      max: limits.max_message_threads_lifetime,
      isLifetime: true,
    });
  } else {
    // Paid tiers show daily usage
    stats.push({
      label: 'Moves generated today',
      current: usage_today.tasks_generated,
      max: limits.max_tasks_per_day,
    });
    stats.push({
      label: 'Message threads today',
      current: usage_today.message_threads_initiated,
      max: limits.max_message_threads_per_day,
    });
  }

  return (
    <section className="rounded-lg border border-neutral-200 bg-primary-10 p-6">
      <h2 className="mb-4 text-lg font-semibold text-neutral-900">
        {tier === 'free' ? 'Usage' : "Today's Usage"}
      </h2>
      <div className="space-y-4">
        {stats.map((stat) => {
          const maxDisplay = stat.max === null ? '∞' : stat.max;
          const percentage =
            stat.max === null ? 0 : (stat.current / stat.max) * 100;

          return (
            <div key={stat.label}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-600">{stat.label}</span>
                <span className="font-medium text-neutral-900">
                  {stat.current} / {maxDisplay}
                </span>
              </div>
              {stat.max !== null && (
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-neutral-200">
                  <div
                    className={`h-full rounded-full transition-all ${
                      percentage >= 100
                        ? 'bg-error-500'
                        : percentage >= 80
                          ? 'bg-warning-500'
                          : 'bg-primary-500'
                    }`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                  />
                </div>
              )}
              {stat.isLifetime && percentage >= 100 && (
                <p className="mt-1 text-xs text-error-600">
                  Limit reached — upgrade to continue
                </p>
              )}
            </div>
          );
        })}
      </div>

      {tier === 'free' && (
        <p className="mt-4 text-xs text-neutral-400">
          Free tier limits do not reset. Upgrade to get daily allowances.
        </p>
      )}
    </section>
  );
}
